import { startTransition, useState } from 'react';
import { isApprovalByTeamEnabled } from '@gonfalon/dogfood-flags/dogfood-flags';
import { MemberMaintainerTag } from '@gonfalon/members/MemberMaintainerTag';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { MultiComboBox, type MultiComboBoxProps } from '@gonfalon/ui/MultiComboBox';
import { Collection, Header, ListBoxSection, Tag } from '@launchpad-ui/components';

import { useMaintainerSelection } from './internal/useMaintainerSelection';
import { getSelectedValues } from './internal/utils';
import { MemberListItem, TeamListItem } from './MaintainerDialog';
import { TeamMaintainerTag } from './TeamMaintainerTag';
import type { MaintainerComboBoxItem, MultipleMaintainerProps, SingleMaintainerProps } from './types';
import type { UseMaintainerItemsParams } from './useMaintainerItems';
import { useMaintainerItems } from './useMaintainerItems';

import styles from './SelectMaintainerTags.module.css';

interface CommonProps
  extends
    Pick<
      UseMaintainerItemsParams,
      'teamsOnly' | 'membersOnly' | 'excludeEmptyTeams' | 'accessChecks' | 'filterOutCurrentMember' | 'filterMembers'
    >,
    Pick<
      MultiComboBoxProps<MaintainerComboBoxItem>,
      | 'placeholder'
      | 'label'
      | 'className'
      | 'onBlur'
      | 'isRequired'
      | 'id'
      | 'onClick'
      | 'description'
      | 'placement'
      | 'isDisabled'
      | 'isClearable'
      | 'renderEmptyState'
    > {
  closeOnSelection?: boolean;
  maxVisibleTags?: number;
}

export type SelectMaintainerTagsProps = (SingleMaintainerProps | MultipleMaintainerProps) & CommonProps;

const getSelectedItems = (params: ReturnType<typeof getSelectedValues>) => {
  const { maintainerId, maintainerTeamKey, maintainerIds = [], maintainerTeamKeys = [] } = params;
  const selectedItems = maintainerIds
    .map((id) => ({
      type: 'member',
      id,
    }))
    .concat(
      maintainerTeamKeys.map((id) => ({
        type: 'team',
        id,
      })),
    );
  if (maintainerId !== undefined) {
    selectedItems.push({
      type: 'member',
      id: maintainerId,
    });
  }

  if (maintainerTeamKey !== undefined) {
    selectedItems.push({
      type: 'team',
      id: maintainerTeamKey,
    });
  }
  return selectedItems;
};

export function SelectMaintainerTags(props: SelectMaintainerTagsProps) {
  const {
    selectionMode,
    filterOutCurrentMember,
    accessChecks,
    teamsOnly,
    membersOnly: membersOnlyProp,
    excludeEmptyTeams,
    filterMembers,
    label,
    className,
    onBlur,
    isRequired,
    maxVisibleTags = -1,
    placeholder,
    onClick,
    description,
    id,
    placement,
    isDisabled,
    isClearable,
    renderEmptyState,
  } = props;
  const [filterText, setFilterText] = useState('');

  const membersOnly = membersOnlyProp ?? !isApprovalByTeamEnabled();

  const selectedValues = getSelectedValues(props);

  const { itemsInSections, isPending: isDataLoading } = useMaintainerItems({
    ...selectedValues,
    filterText,
    teamsOnly,
    membersOnly,
    excludeEmptyTeams,
    accessChecks,
    filterOutCurrentMember,
    filterMembers,
    suspense: false,
  });
  const selectedItems = getSelectedItems(selectedValues);

  const { handleSelectionChange } = useMaintainerSelection({
    itemsInSections,
    ...props,
  });
  const visibleTags = maxVisibleTags > 0 ? selectedItems.slice(0, maxVisibleTags) : selectedItems;
  const selectedKeys = selectedItems.map((item) => item.id);

  return (
    <MultiComboBox<MaintainerComboBoxItem>
      description={description}
      className={className}
      placeholder={placeholder}
      selectionMode={selectionMode}
      isRequired={isRequired}
      onClick={onClick}
      id={id}
      tags={() => (
        <>
          {visibleTags.map((tag) => {
            const { type, id: itemId } = tag;
            if (type === 'member') {
              return <MemberMaintainerTag key={itemId} id={`${type}:${itemId}`} maintainerId={itemId} />;
            }
            return <TeamMaintainerTag key={itemId} id={`${type}:${itemId}`} maintainerTeamKey={itemId} />;
          })}
          {visibleTags.length !== selectedItems.length && (
            <Tag id="more-tags" textValue={msg('maintainers.picker.moreTags')}>
              {() => <>{selectedItems.length - visibleTags.length}</>}
            </Tag>
          )}
        </>
      )}
      label={label}
      selectedKeys={selectedKeys}
      items={itemsInSections}
      filterText={filterText}
      setFilterText={(search) => startTransition(() => setFilterText(search))}
      onSelectionChange={handleSelectionChange}
      renderEmptyState={(inputValue) => {
        if (isDataLoading) {
          return (
            <p>
              <Message id="maintainers.picker.loadingTags" />
            </p>
          );
        }
        return (
          renderEmptyState?.(inputValue) ?? (
            <p>
              {inputValue
                ? msg('maintainers.picker.noResultsFor', { query: inputValue })
                : msg('maintainers.picker.noOptions')}
            </p>
          )
        );
      }}
      onBlur={onBlur}
      placement={placement}
      isDisabled={isDisabled}
      isClearable={isClearable}
    >
      {(section) => {
        const hidden = section.id === 'selected' && visibleTags.length === selectedItems.length;

        return (
          <ListBoxSection hidden={hidden} id={section.id}>
            <Header className={styles.menuSection}>{section.name}</Header>
            <Collection items={section.children}>
              {(item) => {
                return item.type === 'team' ? (
                  <TeamListItem key={item.id} {...item} disabled={hidden} includeTeamCount />
                ) : item.type === 'member' ? (
                  <MemberListItem key={item.id} {...item} disabled={hidden} />
                ) : null;
              }}
            </Collection>
          </ListBoxSection>
        );
      }}
    </MultiComboBox>
  );
}
